"use client"

import Link from "next/link"
import {useActionState, useId} from "react"

import type {LoginState} from "@/app/(auth)/login/actions"
import {loginAction} from "@/app/(auth)/login/actions"
import {Button, Callout, Field, Input} from "@/components/ui"

/**
 * The sign-in form.
 *
 * `useActionState` with a plain `<form action={…}>`, not an onSubmit handler:
 * the form posts natively, so it works before hydration and with JavaScript
 * off. That matters more here than anywhere else in the app — this is the page
 * a publisher reaches when nothing else works yet.
 *
 * `autoComplete` values are the real ones (`username` / `current-password`), so
 * password managers recognise the form. Getting these wrong is why so many
 * internal tools cannot be autofilled.
 */
export const LoginForm = ({
  returnUri,
  registrationEnabled,
}: {
  returnUri: string
  registrationEnabled: boolean
}) => {
  const [state, action, pending] = useActionState<LoginState, FormData>(loginAction, {ok: true})
  const ids = useId()
  const field = (name: string) => `${ids}-${name}`

  return (
    <form action={action} className="flex flex-col gap-5">
      {/*
        The return target rides in the form rather than being read from the URL
        by the action: a Server Action receives only its payload, and the page
        that rendered this form is where the query string lived. It is
        re-validated server-side regardless — see loginAction.
      */}
      <input type="hidden" name="returnUri" value={returnUri} />

      {state.message !== undefined && (
        <Callout tone="danger" title="Could not sign in">
          <p>{state.message}</p>
        </Callout>
      )}

      <Field id={field("email")} label="Email address" error={state.errors?.email}>
        <Input
          id={field("email")}
          name="email"
          type="email"
          required
          autoComplete="username"
          autoFocus
          defaultValue={state.email ?? ""}
          aria-invalid={state.errors?.email !== undefined}
        />
      </Field>

      <Field id={field("password")} label="Password" error={state.errors?.password}>
        <Input
          id={field("password")}
          name="password"
          type="password"
          required
          autoComplete="current-password"
          aria-invalid={state.errors?.password !== undefined}
        />
      </Field>

      <Button type="submit" variant="action" disabled={pending}>
        {pending ? "Signing in…" : "Sign in"}
      </Button>

      {registrationEnabled && (
        <p className="text-sm text-muted-foreground">
          No account yet?{" "}
          <Link href="/register" className="text-link underline underline-offset-4">
            Create one
          </Link>
        </p>
      )}
    </form>
  )
}
